import { Popover } from '@components/popover/Popover';
import { Box, Card, CardContent, CardHeader, Grid, Theme, Typography } from '@mui/material';
import { Filter, Thumbtack, ThumbtackSolid } from '@assets/svgs';

import { Button } from '@components/button/Button';
import { TableProps } from '@components/table/Table';
import { TFilterValues, TTableBase } from '@components/table/types';
import { useSearchParams } from 'react-router-dom';
import { FilterComponent } from '@components/table/TableFilters';
import { useEffect, useMemo, useState } from 'react';
import { Switch } from '@components/switch/Switch';
import { FormControlLabel } from '@components/formControlLabel/FormControlLabel';
import { IconButton } from '@components/iconButton/IconButton';
import { Tooltip } from '@components/tooltip/Tooltip';
import { useTableStore } from '@components/table/StoreTable/TableWithStore';
import { getQueryParamForAppliedFilter } from '@components/table/TableCreateFiltersModal/getQueryParamForAppliedFilter';

export type TableCreateFiltersModal<T extends TTableBase> = {
  onClose: () => void;
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  handleUpdateFilters: (customFilters: TFilterValues) => void;
  filterConfig: TableProps<T>['filterConfig'];
  selectedValues?: { [key: string]: any };
};
export function TableCreateFiltersModal<T extends TTableBase>({
  onClose,
  isOpen,
  anchorEl,
  filterConfig,
  selectedValues,
  handleUpdateFilters,
}: TableCreateFiltersModal<T>) {
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();

  const [formState, setFormState] = useState<any>({});

  const [pinnedFilters = {}, pinnedFiltersEnabled, addPinnedFilter, removePinnedFilter, togglePinnedFilters] =
    useTableStore((state) => [
      state.pinnedFilters,
      state.pinnedFiltersEnabled,
      state.addPinnedFilter,
      state.removePinnedFilter,
      state.togglePinnedFilters,
    ]);

  const selectedValueLength = Object.keys(selectedValues ?? {}).length;

  useEffect(() => {
    const defaultFormState = filterConfig?.filters?.reduce((acc: any, filter) => {
      if (selectedValues && selectedValues[filter.field]) {
        acc[filter.field] = selectedValues[filter.field];
      }
      return acc;
    }, {});

    setFormState(defaultFormState);

    // watch length so if one is removed from custom filters this form-state will rebuild
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig?.filters, selectedValueLength, isOpen]);

  const filterConfigDictionary = useMemo(
    () =>
      filterConfig?.filters?.reduce((acc: any, filter) => {
        acc[filter.field] = filter;
        return acc;
      }, {}),
    [filterConfig?.filters],
  );

  if (!filterConfig || !filterConfig.filters) {
    return null;
  }

  const { filters } = filterConfig;

  const handleResetAllFilters = () => {
    const clearedFilters = filters.reduce((acc: any, filter) => {
      acc[filter.field] = undefined;
      return acc;
    }, {});

    setFormState(pinnedFiltersEnabled ? { ...clearedFilters, ...pinnedFilters } : clearedFilters);
    handleSubmit(clearedFilters);
  };

  const handleSubmit = (appliedFilters: TFilterValues, keepOpen: boolean = false) => {
    Object.entries(appliedFilters).forEach(([key, value]: any) => {
      const queryParam = getQueryParamForAppliedFilter(key, value);

      if (queryParam !== null) {
        searchParamsInTheUrl.set(key, queryParam);
      } else {
        searchParamsInTheUrl.delete(key);
      }
    });
    setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });

    handleUpdateFilters(appliedFilters);

    if (!keepOpen) {
      onClose();
    }
  };

  const handleEnablePinnedFilters = () => {
    togglePinnedFilters(!pinnedFiltersEnabled);

    if (!pinnedFiltersEnabled) {
      const newFormState = { ...formState };
      Object.entries(pinnedFilters).forEach(([key, value]: any) => {
        newFormState[key] = value;

        if (Array.isArray(value) && value.length > 1) {
          // remove any children
          const filter = filterConfigDictionary[key];
          const children = filter?.childrenFields;
          if (children) {
            children.forEach((child: any) => {
              newFormState[child] = undefined;
            });
          }
        }
      });

      setFormState(newFormState);

      // auto apply
      handleSubmit(newFormState, true);
    }
  };

  const isFormStateDirty = true; // EC TODO

  const valuesForForm = isFormStateDirty
    ? pinnedFiltersEnabled
      ? { ...formState, ...pinnedFilters }
      : formState
    : pinnedFiltersEnabled
      ? { ...selectedValues, ...pinnedFilters }
      : selectedValues;

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      doNotShowIconButton
    >
      <Card
        elevation={0}
        sx={[
          sxStyles.cardContainer,
          {
            maxWidth: { xs: '90vw', sm: '100vw' },
            backgroundColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'white' : 'background.main'),
            // on dark palettes the popover is close to the table under it, so give it an edge
            border: (theme: Theme) =>
              theme.palette.theme === 'lio' ? 'none' : `1px solid ${theme.palette.border.default}`,
            '--Paper-overlay': (theme: Theme) =>
              theme.palette.theme !== 'lio'
                ? 'linear-gradient(rgba(255, 255, 255, 0.119), rgba(255, 255, 255, 0.119)) !important'
                : 'none',
          },
        ]}
      >
        <CardHeader
          disableTypography
          title={
            <>
              <Typography variant='h5'>Custom Filters</Typography>
              <Filter />
            </>
          }
        />
        <CardContent>
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch sx={{ m: 1, marginLeft: 0 }} checked={pinnedFiltersEnabled} size='small' />}
                label={
                  <Typography
                    variant='button'
                    sx={{
                      color: (theme) => (theme.palette.theme === 'lio' ? 'primary.dark' : 'text.main'),
                      mr: 1,
                    }}
                  >
                    Enable Pinned Filters
                  </Typography>
                }
                onChange={handleEnablePinnedFilters}
                labelPlacement='start'
                sxProps={{
                  formControlLabel: {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    paddingRight: '10px',
                  },
                }}
              />
            </Grid>
            {filters?.map((filter) => {
              const isPinned = pinnedFiltersEnabled && !!pinnedFilters[filter.field];

              return (
                <Grid
                  key={`filter-${filter.field}`}
                  size={filter.width.gridWidth ?? filter.width.gridWidth}
                  sx={{
                    display: { xs: 'block', sm: 'flex' },
                    maxWidth: '100%',
                    borderTopWidth: '1px',
                    borderColor: 'border.default',
                    borderTopStyle: 'solid',
                    paddingTop: 2,
                    paddingBottom: 0.5,
                    alignItems: 'center',
                    overflowX: 'hidden',
                    backgroundColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'white' : ''),
                  }}
                >
                  <Box sx={{ width: { xs: '100%', sm: '140px' } }}>
                    <Typography
                      variant='bodySmall'
                      sx={{ color: (theme) => (theme.palette.theme === 'lio' ? 'primary.dark' : 'text.main') }}
                      fontWeight='bold'
                    >
                      {filter.label}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <FilterComponent
                      selectedValues={valuesForForm}
                      componentProps={{
                        name: filter.field,
                        watchedField: filter.watchedField,
                        childrenFields: filter.childrenFields,
                        label: '',
                        helperText: filter.helperText,
                        disabled: isPinned || filter?.isDisabled?.(valuesForForm),
                        id: filter.field,
                        value: valuesForForm && valuesForForm[filter.field],
                        onChange: (value: any) => {
                          const fieldsWatching = filters.filter((f) => f.watchedField === filter.field);

                          setFormState((prev: any) => {
                            const newValues = {
                              ...prev,
                              [filter.field]: value,
                            };

                            // clear out any fields watching this one
                            fieldsWatching.forEach((f) => {
                              newValues[f.field] = undefined;
                            });

                            return newValues;
                          });
                        },
                        other: filter?.other,
                        fieldConfig: filter?.fieldConfig,
                      }}
                      type={filter.type}
                    />
                  </Box>

                  {pinnedFiltersEnabled && (
                    <Grid
                      size={{ xs: 1 }}
                      sx={{
                        pl: '8px',
                        width: 48,
                      }}
                    >
                      {valuesForForm[filter.field] && (
                        <Tooltip title={isPinned ? 'Delete Pinned Filter' : 'Create Pinned Filter'}>
                          <IconButton
                            sx={{
                              // primary.dark is a grey that disappears on dark cards, so show pinned in the accent yellow
                              color: (theme: Theme) =>
                                theme.palette.theme === 'lio'
                                  ? 'primary.dark'
                                  : isPinned
                                    ? 'secondary.main'
                                    : 'text.main',
                            }}
                            onClick={() => {
                              if (isPinned) {
                                removePinnedFilter(filter.field);
                              } else {
                                addPinnedFilter({
                                  field: filter.field,
                                  value: valuesForForm[filter.field],
                                });
                              }
                            }}
                          >
                            {isPinned ? <ThumbtackSolid /> : <Thumbtack />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
        <Box sx={sxStyles.cardFooter}>
          <Button variant='text' sx={{ padding: 0 }} onClick={handleResetAllFilters}>
            Reset All Filters
          </Button>
          <Button variant='contained' onClick={() => handleSubmit(valuesForForm)}>
            Apply Filters
          </Button>
        </Box>
      </Card>
    </Popover>
  );
}

// EC: moved here since its only used here and deleted Toolbar.styles.ts
// FUTURE TODO: are these styles even necessary?
export const sxStyles = {
  cardContainer: {
    '&.MuiPaper-elevation0': {
      '&.MuiCard-root': {
        padding: 0,
        borderRadius: '4px',
        overflow: 'hidden',
        width: '520px',
      },
      '& .MuiCardHeader-root': {
        display: 'flex',
        padding: '14px 18px 14px 16px',
        alignItems: 'center',
        backgroundColor: (theme: Theme) => theme.palette.background.main,
        borderBottom: (theme: Theme) => theme.borders.main,
      },
      '& .MuiCardHeader-content': {
        display: 'flex',
        flex: 'none',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      },
      '& .MuiCardContent-root': {
        height: '63vh',
        overflow: 'auto',
        padding: '0px 8px',
      },
    },
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px',
    borderTop: (theme: Theme) => theme.borders.main,
  },
};
